import dayjs from "dayjs";
import { Button, Group, Skeleton, Stack, Text } from "@/components/ui";
import { AccionesDescargaFactura } from "@/features/mis-facturas/components/AccionesDescargaFactura";
import { EstatusFacturaBadge } from "@/features/mis-facturas/components/EstatusFacturaBadge";
import { useFacturasDeSolicitud } from "@/features/mis-facturas/hooks/use-facturas-de-solicitud";
import { serieFolio } from "@/features/mis-facturas/utils/facturas-de-solicitud";
import { extractApiMessage } from "@/shared/api/extract-message";
import { formatearMonto } from "@/shared/format/money";

/**
 * Equivalente mobile de la fila expandible de facturas · lista compacta dentro
 * de la card de la solicitud. Se monta solo al pulsar "Ver facturas" (carga
 * perezosa · misma query que la tabla desktop).
 */
export function FacturasDeSolicitudMobile({ id, solicitudId }: { id: string; solicitudId: string }) {
  const { data, isPending, isError, error, refetch, isFetching } = useFacturasDeSolicitud(solicitudId);

  return (
    <div id={id} className="border-t border-border bg-muted/40 px-4 py-3">
      {isPending ? (
        <Stack gap="xs" aria-hidden="true">
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Stack>
      ) : isError ? (
        <Group gap="sm" justify="space-between" role="status">
          <Text size="sm">No se pudieron cargar las facturas: {extractApiMessage(error, "error desconocido")}</Text>
          <Button size="xs" variant="default" loading={isFetching} onClick={() => void refetch()}>
            Reintentar
          </Button>
        </Group>
      ) : data.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" role="status">
          Esta solicitud no tiene facturas cargadas.
        </Text>
      ) : (
        <ul className="flex flex-col gap-2">
          {data.map((f) => (
            <li key={f.id} className="rounded-md border border-border bg-background p-3">
              <Stack gap={6}>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Text size="sm" fw={600} ff="mono" className="min-w-0 break-all leading-tight text-ink-900 dark:text-ink-50">
                    {f.uuid.toUpperCase()}
                  </Text>
                  <EstatusFacturaBadge estatus={f.estatus} />
                </Group>
                <Text size="sm" fw={600} truncate>
                  {f.nombreEmisor || "—"} <span className="font-normal text-muted-foreground">· {f.rfcEmisor}</span>
                </Text>
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">
                      {serieFolio(f)} · {f.fechaTimbrado ? dayjs(f.fechaTimbrado).format("DD/MM/YYYY HH:mm") : "—"}
                    </Text>
                    <Text size="sm" fw={700}>
                      {formatearMonto(f.total, f.moneda, { minimumFractionDigits: 2 })}
                    </Text>
                  </Stack>
                  <AccionesDescargaFactura factura={f} justify="flex-end" />
                </Group>
              </Stack>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
